import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { Component, ElementRef, Inject, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { Observable } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { ChangeRequestsService } from 'src/app/services/change-requests.service';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MainService } from 'src/app/services/main.service';
import Swal from 'sweetalert2';
import { UsersService } from 'src/app/services/users.service';

@Component({
  selector: 'app-change-requests-add-edit',
  templateUrl: './change-requests-add-edit.component.html',
  styleUrls: ['./change-requests-add-edit.component.scss']
})
export class ChangeRequestsAddEditComponent implements OnInit {
  form!: FormGroup;
  request: any; 
  allBadges: any[] = [];
  allUsers: any[] = [];
  isLoading = false;
  uploadedFiles: any[] = [];

  selectedFile: File | null = null; 
  fileControl = new FormControl();
  finalizada = false;
  
  separatorKeysCodes: number[] = [ENTER, COMMA];
  badgeCtrl = new FormControl();
  filteredBadges!: Observable<string[]>;
  badges: string[] = [];

  @ViewChild('badgeInput') badgeInput!: ElementRef<HTMLInputElement>;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    private dialogRef: MatDialogRef<ChangeRequestsAddEditComponent>,
    private fb: FormBuilder,
    private changeRequestsService: ChangeRequestsService,
    private main: MainService,
     private userService: UsersService,
  ) { }

  ngOnInit(): void {
    this.request = this.data.request;
    this.allBadges = this.data.allBadges || [];
    this.allUsers = this.data.allUsers.filter((x:any)=> x.id_rol !== 3) || [];

  
    if (this.request) {
      this.loadUploadedFiles();
      this.finalizada = this.request.status === 3;
    }

    this.createForm();
    this.setupBadges();
  }

  createForm() {
    let estimatedDate = null;
    if (this.request?.estimated_date) {
      const utcDate = new Date(this.request.estimated_date);
      estimatedDate = new Date(utcDate.getUTCFullYear(), utcDate.getUTCMonth(), utcDate.getUTCDate());
    }else{
      estimatedDate = null;
    }

    this.form = this.fb.group({
      titulo: [this.request?.titulo || '', [Validators.required, Validators.minLength(3)]],
      descripcion: [this.request?.descripcion || ''],
      id_responsable: [this.request?.id_responsable || null],
      status: [this.request?.status || 1],
      estimated_date: [estimatedDate],
    });
  }

  loadUploadedFiles(): void {
    if (!this.request?.id) return;
    this.changeRequestsService.getSolicitudFiles(this.request.id).subscribe({
      next: (res: any) => {
        if (res.message === 'Success') {
          this.uploadedFiles = res.data || [];
        }
      },
      error: (err:any) => console.error('Error al cargar los archivos adjuntos', err)
    });
  }

  setupBadges() {
    this.badges = this.request?.parsed_badges || [];
    this.filteredBadges = this.badgeCtrl.valueChanges.pipe(
      startWith(null),
      map((badge: string | null) => (badge ? this._filter(badge) : this.allBadges.map(b => b.nombre)))
    );
  }

  addBadge(event: any): void {
    const value = (event.value || '').trim();
    if (value && !this.badges.includes(value)) {
      this.badges.push(value);
    } 
    if (event.chipInput) {
      event.chipInput.clear();
    }
    this.badgeCtrl.setValue(null);
  }

  removeBadge(badge: string): void {
    const index = this.badges.indexOf(badge);
    if (index >= 0) {
      this.badges.splice(index, 1);
    }
  }

  selectedBadge(event: MatAutocompleteSelectedEvent): void {
    const value = event.option.viewValue;
    if (!this.badges.includes(value)) {
      this.badges.push(value);
    }
    this.badgeInput.nativeElement.value = '';
    this.badgeCtrl.setValue(null);
  }

  private _filter(value: string): string[] {
    const filterValue = value.toLowerCase();
    return this.allBadges.map(b => b.nombre).filter(badge => badge.toLowerCase().includes(filterValue));
  }

  onFileSelected(event: any) {
    const files = Array.isArray(event) ? event : event.target?.files;
    const file = files?.[0] || null;

    this.selectedFile = file;
  }

  hasPermissions(){
   return this.userService.isBuhoUsr();
  }

  private formatDateToYMD(date: Date | string | null): string | null {
    if (!date) return null;
    const d = new Date(date);
  d.setHours(d.getHours() + 24);
    const year = d.getUTCFullYear();
    const month = `${d.getUTCMonth() + 1}`.padStart(2, '0');
    const day = `${d.getUTCDate()}`.padStart(2, '0');
    return `${year}-${month}-${day}`;
  }


  async onSave() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this.isLoading = true;
    this.main.ShowLoading();


    const existingBadgeNames = this.allBadges.map(b => b.nombre.toLowerCase());
    const newBadges = this.badges.filter(b => !existingBadgeNames.includes(b.toLowerCase()));

    if (newBadges.length > 0) {
      for (const badgeName of newBadges) {
        try {
          const res: any = await this.changeRequestsService.createBadge({ nombre: badgeName }).toPromise();
          if (res.message === 'Success' && res.data) {
            this.allBadges.push(res.data); 
          }
        } catch (error) {
          console.error(`Error creating badge ${badgeName}:`, error);
        }
      }
    }

 
    const requestData = {
      ...this.form.value,
      badges: JSON.stringify(this.badges),
      estimated_date: this.formatDateToYMD(this.form.value.estimated_date)
    };

    if (this.request) { // --- MODO EDICIÓN ---
      requestData.id = this.request.id;
      const updatePromises: Promise<any>[] = [];

      if (this.form.get('titulo')?.dirty || this.form.get('descripcion')?.dirty) {
        updatePromises.push(this.changeRequestsService.updateSolicitud({
          id: this.request.id,
          titulo: requestData.titulo,
          descripcion: requestData.descripcion
        }).toPromise());
      }

      const originalBadges = this.request?.parsed_badges || [];
      const badgesChanged = JSON.stringify([...originalBadges].sort()) !== JSON.stringify([...this.badges].sort());
      if (badgesChanged) {
        updatePromises.push(this.changeRequestsService.updateBadges({
          id_solicitud: this.request.id,
          badges: JSON.stringify(this.badges),
          new_badges: JSON.stringify(this.badges)
        }).toPromise());
      }

      if (this.form.get('id_responsable')?.dirty && this.request.id_responsable !== requestData.id_responsable) {
        const responsableName = this.allUsers.find(u => u.id === requestData.id_responsable)?.name || 'Sin asignar';
        updatePromises.push(this.changeRequestsService.setResponsable({
          id_solicitud: this.request.id,
          id_responsable: requestData.id_responsable,
          responsable: responsableName
        }).toPromise());
      }

      let specificFieldsChangedCount = 0;
      const combinedUpdateData: any = { id_solicitud: this.request.id };

      const originalDate = this.request.estimated_date ? new Date(this.request.estimated_date).toISOString().split('T')[0] : null;
      const newDate = requestData.estimated_date;
      if (this.form.get('estimated_date')?.dirty && originalDate !== newDate) {
        specificFieldsChangedCount++;
        combinedUpdateData.estimated_date = newDate;
      }

      if (this.form.get('status')?.dirty && this.request.status !== requestData.status) {
        specificFieldsChangedCount++;
        combinedUpdateData.status = requestData.status;
        combinedUpdateData.status_name = this.getStatusName(requestData.status); 
      }

      if (specificFieldsChangedCount === 2) { 
        updatePromises.push(this.changeRequestsService.editSolicitud(combinedUpdateData).toPromise());
      } else if (specificFieldsChangedCount === 1) {
        if (combinedUpdateData.estimated_date !== undefined) updatePromises.push(this.changeRequestsService.setEstimatedDate({ id_solicitud: this.request.id, date: combinedUpdateData.estimated_date }).toPromise());
        if (combinedUpdateData.status !== undefined) updatePromises.push(this.changeRequestsService.setSolicitudStatus({ id_solicitud: this.request.id, status: combinedUpdateData.status, status_name: combinedUpdateData.status_name }).toPromise());
      }

      Promise.all(updatePromises)
        .then(() => this.handleSaveResponse({ message: 'Success' }))
        .catch(err => this.handleSaveError(err));

    } else { // --- MODO CREACIÓN ---
      this.changeRequestsService.createSolicitud(requestData).subscribe({
        next: (res: any) => this.handleSaveResponse(res),
        error: (err: any) => this.handleSaveError(err)
      });
    }
  }

  handleSaveResponse(res: any) {
    if (res.message === 'Success') {
      const solicitudId = this.request?.id || res.id_solicitud;
      if (this.selectedFile && solicitudId) {
        this.uploadImage(solicitudId);
      } else {
        this.isLoading = false;
        this.main.HideLoading();
        Swal.fire('Éxito', 'Solicitud guardada correctamente.', 'success');
        this.dialogRef.close({ reload: true });
      }
    } else {
      this.handleSaveError(res.message || 'Error desconocido');
    }
  }

  uploadImage(solicitudId: number) {
    const formData = new FormData();
    formData.append('id_solicitud', solicitudId.toString());
    formData.append('file', this.selectedFile!);

    this.changeRequestsService.uploadImage(formData).subscribe({
      next: (res: any) => {
        this.isLoading = false;
        this.main.HideLoading();
        if (res.message === 'Success') {
          Swal.fire('Éxito', 'Solicitud y archivos guardados correctamente.', 'success');
          this.dialogRef.close({ reload: true });
        } else {
          Swal.fire('Advertencia', 'La solicitud se guardó, pero hubo un error al subir el archivo.', 'warning');
          this.dialogRef.close({ reload: true });
        }
      },
      error: (err) => {
        this.isLoading = false;
        this.main.HideLoading();
        Swal.fire('Advertencia', 'La solicitud se guardó, pero hubo un error al subir el archivo.', 'warning');
        console.error(err);
        this.dialogRef.close({ reload: true });
      }
    });
  }

  handleSaveError(err: any) {
    this.isLoading = false;
    this.main.HideLoading();
    console.error(err);
    Swal.fire('Error', 'No se pudo guardar la solicitud.', 'error');
  }

  getStatusName(status: number): string {
    switch (status) {
      case 1: return 'Pendiente';
      case 2: return 'En Proceso';
      case 3: return 'Terminada';
      default: return 'Desconocido';
    }
  }

}
